import { LatLon } from '../domain/types.ts';

export interface LocationResult {
  coords: LatLon;
  label: string;
  region: 'north' | 'central' | 'south';
}

interface NominatimResponseItem {
  lat: string;
  lon: string;
  display_name: string;
}

export class SearchService {
  private static STORAGE_KEY = 'dogpoop_recent_searches_v1';

  // 20 địa điểm nổi tiếng Việt Nam phân bổ theo 3 miền
  public static readonly FALLBACK_LOCATIONS: LocationResult[] = [
    // Miền Bắc
    { coords: { lat: 21.0285, lon: 105.8542 }, label: 'Hồ Hoàn Kiếm, Hoàn Kiếm, Hà Nội', region: 'north' },
    { coords: { lat: 21.058, lon: 105.8139 }, label: 'Hồ Tây, Tây Hồ, Hà Nội', region: 'north' },
    { coords: { lat: 21.0338, lon: 105.8504 }, label: 'Phố Cổ Tạ Hiện, Hoàn Kiếm, Hà Nội', region: 'north' },
    { coords: { lat: 21.0368, lon: 105.7905 }, label: 'Ngõ 165 Cầu Giấy, Cầu Giấy, Hà Nội', region: 'north' },
    { coords: { lat: 20.9722, lon: 105.8291 }, label: 'Bán Đảo Linh Đàm, Hoàng Mai, Hà Nội', region: 'north' },
    { coords: { lat: 21.0031, lon: 105.8431 }, label: 'Khu Tập Thể Bách Khoa, Hai Bà Trưng, Hà Nội', region: 'north' },
    { coords: { lat: 20.8449, lon: 106.6881 }, label: 'Nhà Hát Lớn Hải Phòng, Hồng Bàng, Hải Phòng', region: 'north' },

    // Miền Trung & Tây Nguyên
    { coords: { lat: 16.068, lon: 108.2208 }, label: 'Cầu Rồng, Hải Châu, Đà Nẵng', region: 'central' },
    { coords: { lat: 16.0592, lon: 108.2435 }, label: 'Bãi biển Mỹ Khê, Ngũ Hành Sơn, Đà Nẵng', region: 'central' },
    { coords: { lat: 15.8801, lon: 108.338 }, label: 'Phố Cổ Hội An, Quảng Nam', region: 'central' },
    { coords: { lat: 16.4637, lon: 107.5909 }, label: 'Đại Nội Huế, Thừa Thiên Huế', region: 'central' },
    { coords: { lat: 11.9404, lon: 108.4377 }, label: 'Chợ Đà Lạt, Lâm Đồng', region: 'central' },
    { coords: { lat: 12.2388, lon: 109.1967 }, label: 'Quảng trường 2/4, Nha Trang, Khánh Hòa', region: 'central' },

    // Miền Nam
    { coords: { lat: 10.7769, lon: 106.7009 }, label: 'Chợ Bến Thành, Quận 1, TP. Hồ Chí Minh', region: 'south' },
    { coords: { lat: 10.7745, lon: 106.7036 }, label: 'Phố Đi Bộ Nguyễn Huệ, Quận 1, TP. Hồ Chí Minh', region: 'south' },
    { coords: { lat: 10.7936, lon: 106.7218 }, label: 'Landmark 81, Bình Thạnh, TP. Hồ Chí Minh', region: 'south' },
    { coords: { lat: 10.7721, lon: 106.6983 }, label: 'Dinh Độc Lập, Quận 1, TP. Hồ Chí Minh', region: 'south' },
    { coords: { lat: 10.7552, lon: 106.6575 }, label: 'Chợ Lớn (Bình Tây), Quận 6, TP. Hồ Chí Minh', region: 'south' },
    { coords: { lat: 10.0452, lon: 105.7469 }, label: 'Bến Ninh Kiều, Ninh Kiều, Cần Thơ', region: 'south' },
    { coords: { lat: 10.346, lon: 107.0843 }, label: 'Bãi Sau, Vũng Tàu, Bà Rịa - Vũng Tàu', region: 'south' },
  ];

  public static detectRegion(label: string): 'north' | 'central' | 'south' {
    const l = label.toLowerCase();
    if (
      l.includes('hà nội') ||
      l.includes('hanoi') ||
      l.includes('ha noi') ||
      l.includes('hải phòng') ||
      l.includes('hai phong') ||
      l.includes('quảng ninh') ||
      l.includes('cầu giấy') ||
      l.includes('hoàn kiếm') ||
      l.includes('tây hồ') ||
      l.includes('bắc')
    ) {
      return 'north';
    }
    if (
      l.includes('đà nẵng') ||
      l.includes('da nang') ||
      l.includes('danang') ||
      l.includes('huế') ||
      l.includes('hue') ||
      l.includes('hội an') ||
      l.includes('hoi an') ||
      l.includes('quảng nam') ||
      l.includes('đà lạt') ||
      l.includes('da lat') ||
      l.includes('dalat') ||
      l.includes('nha trang') ||
      l.includes('khánh hòa') ||
      l.includes('bình định')
    ) {
      return 'central';
    }
    return 'south';
  }

  public static async searchLocations(query: string, timeoutMs = 5000): Promise<LocationResult[]> {
    const cleanQuery = query.trim();
    if (!cleanQuery) return [];

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        cleanQuery
      )}&countrycodes=vn&limit=5`;

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept-Language': 'vi,en;q=0.9',
        },
      });

      clearTimeout(timer);

      if (response.ok) {
        const data = (await response.json()) as NominatimResponseItem[];
        if (Array.isArray(data) && data.length > 0) {
          return data.map((item) => ({
            coords: {
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
            },
            label: item.display_name,
            region: SearchService.detectRegion(item.display_name),
          }));
        }
      }
    } catch {
      // Fallback khi offline hoặc timeout
    } finally {
      clearTimeout(timer);
    }

    // Lọc fallback theo từ khóa tìm kiếm
    const lower = cleanQuery.toLowerCase();
    const matched = SearchService.FALLBACK_LOCATIONS.filter((item) =>
      item.label.toLowerCase().includes(lower)
    );

    return matched.length > 0 ? matched : SearchService.FALLBACK_LOCATIONS.slice(0, 5);
  }

  private static RECENT_MAP_KEY = 'dogpoop_last_played_map_v1';

  public static getRecentSearches(): LocationResult[] {
    try {
      const raw = localStorage.getItem(SearchService.STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {
      // Ignore
    }
    return [];
  }

  public static saveRecentSearch(loc: LocationResult): void {
    try {
      const current = SearchService.getRecentSearches().filter(
        (x) => x.label !== loc.label
      );
      current.unshift(loc);
      localStorage.setItem(
        SearchService.STORAGE_KEY,
        JSON.stringify(current.slice(0, 6))
      );
    } catch {
      // Ignore
    }
  }

  public static getLastPlayedMap(): {
    mapId: import('../domain/types.ts').MapId;
    label: string;
    region: 'north' | 'central' | 'south';
    paranoia?: number;
    aiConditions?: import('../services/aiMapGenerator.ts').GeneratedConditions;
  } | null {
    try {
      const raw = localStorage.getItem(SearchService.RECENT_MAP_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {
      // Ignore
    }
    return null;
  }

  public static saveLastPlayedMap(data: {
    mapId: import('../domain/types.ts').MapId;
    label: string;
    region: 'north' | 'central' | 'south';
    paranoia?: number;
    aiConditions?: import('../services/aiMapGenerator.ts').GeneratedConditions;
  }): void {
    try {
      localStorage.setItem(SearchService.RECENT_MAP_KEY, JSON.stringify(data));
    } catch {
      // Ignore
    }
  }
}
